import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";

/**
 * Misma lógica que PagosEdificio / NovedadesEdificio: prioriza REACT_APP_PROPIA_API_URL,
 * si no, deriva https desde REACT_APP_PROPIA_WS_URL (fallback producción como el resto del front).
 * Nota: las env vars y la URL Cloud Run mantienen el prefijo "propia*" porque renombrar
 * la infraestructura desplegada queda fuera de alcance del rebranding a Habi.
 */
function resolveHabiApiBaseUrl() {
  if (process.env.REACT_APP_PROPIA_API_URL) {
    return process.env.REACT_APP_PROPIA_API_URL.replace(/\/$/, "");
  }
  const wsUrl =
    process.env.REACT_APP_PROPIA_WS_URL ||
    "wss://propia-agent-241529496588.us-central1.run.app";
  return wsUrl.replace("wss://", "https://").replace("ws://", "http://").replace("/ws", "");
}

export const HABI_API_BASE_URL = resolveHabiApiBaseUrl();

/**
 * Obtiene el token de Firebase del usuario actual.
 * Si auth.currentUser es null (session aun restaurándose), espera hasta 5s.
 */
export const getAuthToken = async () => {
  let user = auth.currentUser;
  if (!user) {
    // Esperar a que Firebase restaure la sesion (puede tardar tras recarga)
    user = await new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(null), 5000);
      const unsub = auth.onAuthStateChanged((u) => {
        clearTimeout(timeout);
        unsub();
        resolve(u);
      });
    });
  }
  if (!user) throw new Error("No hay usuario autenticado. Intentá recargar la página.");
  return user.getIdToken();
};

/**
 * Retorna headers con Authorization Bearer token para llamadas a Habi
 */
export const getAuthHeaders = async () => {
  const token = await getAuthToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Mensaje que mandó el servidor en el error de una callable, o "" si no vino
 * ninguno. Cuando la respuesta no trae el error del servidor, el SDK de
 * functions usa como mensaje el código pelado ("not-found", "NOT_FOUND",
 * "internal"): pasa, por ejemplo, con una función que no está desplegada
 * (404 sin cuerpo). Un HttpsError del servidor sí trae su texto (p. ej.
 * not-found "La incidencia no existe.").
 *
 * @param {any} e - Error de httpsCallable
 * @returns {string}
 */
function mensajeDelServidor(e) {
  const msg = String(e?.message || "").trim();
  if (!msg) return "";
  const codigo = String(e?.code || "").replace(/^functions\//, "");
  const pelado = (s) => s.toUpperCase().replace(/[-\s]/g, "_");
  return codigo && pelado(msg) === pelado(codigo) ? "" : msg;
}

/**
 * Sprint 3 — Envia mensaje de cobranza usando template aprobado por Meta.
 *
 * Internamente invoca la callable `sendCollectionMessage` (functions/index.js)
 * que aplica `notifyUser()`: respeta preferencias del residente, quiet hours,
 * rate-limit, fallback email automatico, e idempotencia por
 * (buildingId, userid, period_key, stage).
 *
 * @param {object} params
 * @param {string} params.buildingId
 * @param {string|number} params.userid - userid numerico del residente
 * @param {number} params.stage - 1..4 (etapa 5 no manda WhatsApp)
 * @param {string} params.messageOverride - texto editado por el admin (opcional)
 * @returns {Promise<{success, channel, dedupe?: boolean}>}
 */
export async function sendCollectionMessageCallable({ buildingId, userid, stage, messageOverride }) {
  try {
    const fn = httpsCallable(functions, "sendCollectionMessage");
    const result = await fn({
      building_id: String(buildingId),
      userid: String(userid),
      stage,
      message_override: messageOverride || null,
    });
    return result.data;
  } catch (e) {
    const code = e?.code || "";
    const msg = mensajeDelServidor(e);
    if (code === "functions/not-found") {
      throw new Error(msg || "sendCollectionMessage no está desplegada. Ejecutá: npm run deploy:functions");
    }
    if (code === "functions/failed-precondition") {
      const data = e?.details || {};
      // Deuda solo de alquiler (unidad "Alquiler del edificio"): las etapas son de
      // gastos comunes y no se le mandan por ningún canal (sin fallback a texto libre).
      if (data.motivo === "solo_alquiler") {
        const err = new Error(msg || "Es deuda de alquiler: las etapas de cobranza son de gastos comunes.");
        err.motivo = "solo_alquiler";
        throw err;
      }
      // El backend nos dice que tenemos que usar fallback (no hay template approved + enabled)
      const err = new Error(msg || "No hay template aprobado y habilitado para este edificio.");
      err.shouldFallback = true;
      err.fallbackReason = data.reason || "no_approved_template";
      throw err;
    }
    throw new Error(msg || "No se pudo enviar el mensaje de cobranza.");
  }
}

/**
 * Bandeja WhatsApp — toma ("take") o devuelve ("release") el control de una
 * conversacion (callable `whatsappSetThreadMode`). Al tomar, Habi queda pausada
 * en ese chat; al devolver, la IA retoma.
 *
 * @param {string} phone  - Telefono del contacto (E.164 o formato uruguayo)
 * @param {"take"|"release"} action
 * @returns {Promise<{ success: boolean, mode: "human"|"ai" }>}
 */
export async function whatsappSetThreadMode(phone, action) {
  try {
    const fn = httpsCallable(functions, "whatsappSetThreadMode");
    const result = await fn({ phone, action });
    return result.data;
  } catch (e) {
    const code = e?.code || "";
    const msg = mensajeDelServidor(e);
    if (code === "functions/permission-denied") {
      throw new Error(msg || "No tenés permiso sobre esta conversación.");
    }
    if (code === "functions/not-found") {
      throw new Error(msg || "No se encontró la conversación (¿función desplegada?).");
    }
    throw new Error(msg || "No se pudo cambiar el control de la conversación.");
  }
}

/**
 * Bandeja WhatsApp — marca una conversacion como leida (resetea unreadCount).
 * Se llama al abrir un hilo. Best-effort: si falla, no rompe la UI.
 *
 * @param {string} phone - Telefono del contacto
 * @returns {Promise<void>}
 */
export async function whatsappMarkThreadRead(phone) {
  try {
    const fn = httpsCallable(functions, "whatsappMarkThreadRead");
    await fn({ phone });
  } catch (e) {
    console.warn("whatsappMarkThreadRead fallo (no crítico):", e?.message || e);
  }
}

/**
 * Bandeja WhatsApp — envia una respuesta manual (texto libre) al contacto
 * (callable `whatsappSendManualReply`). Toma la conversacion automaticamente.
 * Solo funciona dentro de la ventana de 24h de Meta.
 *
 * @param {string} phone - Telefono del contacto
 * @param {string} text  - Texto a enviar
 * @returns {Promise<{ success: boolean, messageId?: string }>}
 */
export async function whatsappSendManualReply(phone, text) {
  try {
    const fn = httpsCallable(functions, "whatsappSendManualReply");
    const result = await fn({ phone, text });
    return result.data;
  } catch (e) {
    const code = e?.code || "";
    const msg = mensajeDelServidor(e);
    if (code === "functions/failed-precondition") {
      // Fuera de la ventana de 24h: el front debe sugerir una plantilla.
      const err = new Error(
        msg ||
          "El contacto no escribió en las últimas 24 h. Usá una plantilla aprobada."
      );
      err.outsideWindow = true;
      throw err;
    }
    if (code === "functions/permission-denied") {
      throw new Error(msg || "No tenés permiso sobre esta conversación.");
    }
    if (code === "functions/unauthenticated") {
      throw new Error("Iniciá sesión para responder.");
    }
    throw new Error(msg || "No se pudo enviar la respuesta.");
  }
}

/**
 * Bandeja WhatsApp — manda al residente de la conversación un LINK para crear
 * su contraseña nueva (callable `whatsappSendPasswordResetLink`): por WhatsApp
 * (ventana de 24 h) y por email. Nunca se escriben contraseñas en el chat.
 *
 * @param {string} phone - Telefono del contacto
 * @returns {Promise<{ success: boolean, emailMasked?: string }>}
 */
export async function whatsappSendPasswordResetLink(phone) {
  try {
    const fn = httpsCallable(functions, "whatsappSendPasswordResetLink");
    const result = await fn({ phone });
    return result.data;
  } catch (e) {
    throw new Error(e?.message || "No se pudo enviar el link de contraseña.");
  }
}

/**
 * Error claro para un envío por la callable `sendIncidentResponseWhatsApp`.
 * Conserva el `code` de la callable (p. ej. "functions/failed-precondition")
 * para que la pantalla decida el respaldo (wa.me).
 *
 * @param {any} e - Error de httpsCallable
 * @returns {Error & { code: string }}
 */
function errorDeEnvioWhatsApp(e) {
  const code = e?.code || "";
  const msg = mensajeDelServidor(e);
  let texto;
  if (code === "functions/unauthenticated") {
    texto = "Iniciá sesión para enviar WhatsApp.";
  } else if (code === "functions/invalid-argument") {
    texto = msg || "Datos inválidos para enviar WhatsApp.";
  } else if (code === "functions/failed-precondition") {
    texto =
      msg || "WhatsApp no configurado. Configurá WHATSAPP_TOKEN y WHATSAPP_PHONE_NUMBER_ID en Firebase Secrets.";
  } else if (code === "functions/not-found") {
    // Con mensaje es el servidor (p. ej. "La incidencia no existe."); sin
    // mensaje, la función no respondió: no está desplegada.
    texto = msg || "La función sendIncidentResponseWhatsApp no está desplegada. Corré: npm run deploy:functions:whatsapp";
  } else {
    texto = msg || "No se pudo enviar el mensaje de WhatsApp.";
  }
  return Object.assign(new Error(texto), { code });
}

/** true si el valor viene cargado (0 cuenta; null, undefined y "" no). */
const presente = (v) => v != null && String(v).trim() !== "";

/**
 * Envía la respuesta de una incidencia por WhatsApp directamente al residente,
 * con la Firebase Callable `sendIncidentResponseWhatsApp` (Meta Cloud API).
 *
 * Contrato C3: el servidor exige incidentId o buildingId (sin ninguno no envía:
 * invalid-argument), graba el saliente en `messages` con el edificio del envío
 * (el de la incidencia o este buildingId, y si vienen los dos tienen que
 * coincidir) y comprueba que quien llama es staff de ESE edificio. Con
 * incidentId el destinatario es quien reportó la incidencia y su celular lo
 * resuelve el servidor: `phone` se ignora y puede venir vacío (no se manda).
 * Para un recordatorio de Cobranza, usar sendCollectionWhatsApp (por userid).
 *
 * @param {string | null} phone - Número del residente (formatos uruguayos); opcional con incidentId
 * @param {string} message - Texto a enviar
 * @param {{ buildingId?: string | number | null, incidentId?: string | null }} [contexto]
 *   - Edificio desde el que se envía y, si es la respuesta a una incidencia, su id
 * @returns {Promise<{ success: boolean, messageId?: string }>}
 * @throws Error con mensaje descriptivo (y el `code` de la callable) si falla
 */
export async function sendIncidentWhatsAppDirect(phone, message, { buildingId = null, incidentId = null } = {}) {
  try {
    const fn = httpsCallable(functions, "sendIncidentResponseWhatsApp");
    const payload = { message };
    if (presente(phone)) payload.phone = phone;
    if (presente(buildingId)) payload.buildingId = buildingId;
    if (presente(incidentId)) payload.incidentId = String(incidentId);
    const result = await fn(payload);
    return result.data;
  } catch (e) {
    throw errorDeEnvioWhatsApp(e);
  }
}

/**
 * Cobranza — recordatorio por WhatsApp (texto libre, dentro de la ventana de
 * 24 h de Meta) a un residente, por su userid (contrato K3).
 *
 * Llama a `sendIncidentResponseWhatsApp` con { buildingId, userid, message } y
 * SIN teléfono: el servidor resuelve el celular de la ficha vigente del
 * residente cuyo accessBids incluye ese edificio (como hace con quien reportó
 * una incidencia) e ignora cualquier número del cliente, salvo para el
 * superadmin. Así el staff no elige a qué número sale un texto libre de la
 * WABA. El saliente queda en `messages` con ese buildingId (vigilancia de
 * entrega, C3).
 *
 * @param {{ buildingId: string | number, userid: string | number, message: string }} params
 * @returns {Promise<{ success: boolean, messageId?: string, buildingId?: number }>}
 * @throws Error con mensaje descriptivo (y el `code` de la callable) si falla;
 *   sin edificio o sin residente no llama al servidor
 */
export async function sendCollectionWhatsApp({ buildingId, userid, message } = {}) {
  if (!presente(buildingId)) throw new Error("Falta el edificio del recordatorio.");
  if (!presente(userid)) throw new Error("Falta el residente del recordatorio.");
  try {
    const fn = httpsCallable(functions, "sendIncidentResponseWhatsApp");
    const result = await fn({ buildingId, userid: String(userid).trim(), message });
    return result.data;
  } catch (e) {
    throw errorDeEnvioWhatsApp(e);
  }
}

/**
 * Enriquece un padrón (o dirección) con datos abiertos de la IM + Vivienda
 * Promovida + valor catastral scrapeado + scoring. Invoca la callable
 * `enrichPadron` (functions/index.js). Pasá { padron } o { calle, puerta }.
 */
// URL del microservicio de prospección en Cloud Run (patrón catastro). Setear
// REACT_APP_PROSPECCION_API_URL con la URL real tras el primer deploy.
const PROSPECCION_API_URL =
  process.env.REACT_APP_PROSPECCION_API_URL ||
  "https://prospeccion-service-241529496588.us-central1.run.app";

/**
 * Genera con IA (Habi) los textos personalizados de una propuesta comercial:
 * la bajada de portada, la bajada del precio y los párrafos "cómo te resuelve
 * tu edificio", a partir de catastro + servicios + perfil + el contexto libre
 * que describe el comercial. Devuelve { coverLead, precioLead, secciones }.
 * Si el backend no está disponible (p. ej. propia_agent sin redeploy), el
 * llamador cae al generador heurístico local (presupuestoPersonalizacion.js).
 */
export async function generarPropuestaIA(payload) {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${HABI_API_BASE_URL}/api/generate-proposal`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    let msg = "No se pudo generar la propuesta con IA.";
    try { const j = await resp.json(); msg = j.detail || j.error || msg; } catch (_) {}
    const err = new Error(msg); err.status = resp.status; throw err;
  }
  return resp.json(); // { coverLead, precioLead, secciones: [{titulo, cuerpo}] }
}

// ── Equipo de Marketing Agéntico (SuperAdmin) ────────────────────────────────
/** Dispara un ciclo del equipo de marketing. Respeta el tope de US$20/mes en el
 * backend; devuelve { status, item_id, pieza, budget } o { status:"budget_exhausted" }. */
export async function correrCicloMarketing({ foco = null, canal = "instagram", model = null } = {}) {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${HABI_API_BASE_URL}/api/marketing/run`, {
    method: "POST",
    headers,
    body: JSON.stringify({ foco, canal, model }),
  });
  if (!resp.ok) {
    let msg = "No se pudo correr el equipo de marketing.";
    try { const j = await resp.json(); msg = j.detail || j.error || msg; } catch (_) {}
    const err = new Error(msg); err.status = resp.status; throw err;
  }
  return resp.json();
}

/** Estado del organigrama: agentes, corridas, piezas pendientes y presupuesto. */
export async function estadoMarketing() {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${HABI_API_BASE_URL}/api/marketing/status`, { headers });
  if (!resp.ok) throw new Error("No se pudo leer el estado del equipo de marketing.");
  return resp.json();
}

/** Checkpoint humano: aprueba o descarta una pieza que estaba esperando_ok. */
export async function revisarPiezaMarketing(itemId, accion = "aprobar") {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${HABI_API_BASE_URL}/api/marketing/approve/${encodeURIComponent(itemId)}`, {
    method: "POST",
    headers,
    body: JSON.stringify({ accion }),
  });
  if (!resp.ok) {
    let msg = "No se pudo actualizar la pieza.";
    try { const j = await resp.json(); msg = j.detail || j.error || msg; } catch (_) {}
    throw new Error(msg);
  }
  return resp.json();
}

// ── Gestor de Pauta (SuperAdmin) ─────────────────────────────────────────────
async function _pautaFetch(path, { method = "GET", body } = {}, errMsg) {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${HABI_API_BASE_URL}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!resp.ok) {
    let msg = errMsg;
    try { const j = await resp.json(); msg = j.detail || j.error || msg; } catch (_) {}
    const err = new Error(msg); err.status = resp.status; throw err;
  }
  return resp.json();
}

/** Estado del gestor: config + cache de insights + últimas acciones (solo Firestore). */
export const estadoPauta = () =>
  _pautaFetch("/api/pauta/status", {}, "No se pudo leer el estado de pauta.");

/** Fuerza el refresh del cache de insights contra Meta. */
export const refrescarPauta = () =>
  _pautaFetch("/api/pauta/refresh", { method: "POST", body: {} }, "No se pudo refrescar la pauta.");

/** Corre un ciclo del gestor a mano (modo diario|semanal). */
export const correrCicloPauta = (modo = "semanal") =>
  _pautaFetch("/api/pauta/run", { method: "POST", body: { modo } }, "No se pudo correr el gestor de pauta.");

/** Guarda config del gestor (kill_switch, dry_run, topes, LTV...). */
export const guardarConfigPauta = (config) =>
  _pautaFetch("/api/pauta/config", { method: "POST", body: { config } }, "No se pudo guardar la config.");

/** Aprueba o descarta una acción esperando_ok (followup / budget / pausa). */
export const revisarAccionPauta = (actionId, accion = "aprobar") =>
  _pautaFetch(`/api/pauta/review/${encodeURIComponent(actionId)}`,
    { method: "POST", body: { accion } }, "No se pudo revisar la acción.");

/** Mapeo manual ad → público/ángulo (respaldo de la convención de nombres). */
export const asignarAnguloAd = (adId, publico, angulo) =>
  _pautaFetch("/api/pauta/ad-angulo",
    { method: "POST", body: { ad_id: adId, publico, angulo } }, "No se pudo asignar el ángulo.");

export async function enrichPadronCallable({ padron, calle, puerta, refresh } = {}) {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${PROSPECCION_API_URL}/api/prospeccion/enrich`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify({ padron, calle, puerta, refresh }),
  });
  if (!resp.ok) {
    if (resp.status === 401) throw new Error("Iniciá sesión para consultar prospección.");
    let msg = "No se pudo enriquecer el padrón.";
    try { const j = await resp.json(); msg = j.error || msg; } catch (_) {}
    throw new Error(msg);
  }
  return resp.json();
}
